import { useMemo, useCallback, useState, useEffect } from 'react';
import { markOnboardingStep } from '../utils/onboarding';
import { FiBell, FiDownload, FiCalendar, FiAlertTriangle, FiClock, FiTrendingUp, FiCheckCircle, FiFileText, FiSearch, FiX } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import Chip from '../components/common/Chip';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import { IlloAllClear, IlloSearch } from '../components/common/Illustrations';
import { useUI } from '../context/UIContext';
import { useCompanyDeadlines } from '../hooks/useCompanyDeadlines';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/constants';

// Urgency chip — tone follows how close (or past) the deadline is.
function daysChip(days) {
  if (days < 0) return { label: `Overdue ${Math.abs(days)}d`, bg: '#f6e9e6', color: '#9a564a' };
  if (days <= 7) return { label: `${days}d left`, bg: '#f3ebd7', color: '#8a6a1f' };
  if (days <= 30) return { label: `${days}d left`, bg: '#e8ecf3', color: '#3a4a66' };
  return { label: `${days}d left`, bg: '#e9f0e4', color: '#4a6b3f' };
}

function locationOf(row) {
  if (row.city && row.state && row.city.toLowerCase() !== row.state.toLowerCase()) return `${row.city}, ${row.state}`;
  return row.city || row.state || '';
}

export default function AlertsPage() {
  const { showToast } = useUI();
  const navigate = useNavigate();
  const { alerts } = useCompanyDeadlines();
  const [selectedFilter, setSelectedFilter] = useState('all'); // all, overdue, week, month
  const [searchQuery, setSearchQuery] = useState('');

  // Getting-started runway: visiting this page completes "Review your alerts".
  useEffect(() => { markOnboardingStep('visited-alerts'); }, []);

  // Normalize every alert once.
  const allRows = useMemo(() => alerts.map((a, i) => ({
    id: a.id || `a-${i}`,
    company: a.companyName || 'Company',
    type: a.companyType || 'Pvt Ltd',
    // Rule-book obligations often have no form code — many statutes prescribe
    // a duty, not a form — so joining unconditionally leaves a leading space.
    compliance: [a.formCode, a.item].filter(Boolean).join(' '),
    item: a.item || '',
    section: a.section || '',
    dueDate: a.dueDate || '—',
    days: a.daysRemaining ?? 0,
    penalty: a.penalty || 0,
    penaltyPerDay: a.penaltyPerDay || 0,
    penaltyNote: a.penaltyNote || '',
    action: (a.daysRemaining ?? 0) < 0 ? 'File Now' : 'Prepare',
    estimated: a.estimated || false,
    agmNotSet: a.agmNotSet || false,
    agmNotSetForCurrentFy: a.agmNotSetForCurrentFy || false,
    city: a.companyCity || '',
    state: a.companyState || '',
    formCode: a.formCode,
    companyId: a.companyId,
  })), [alerts]);

  // Rows after the severity filter + free-text search (company / compliance).
  const rows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allRows.filter((r) => {
      if (selectedFilter === 'overdue' && !(r.days < 0)) return false;
      if (selectedFilter === 'week' && !(r.days >= 0 && r.days <= 7)) return false;
      if (selectedFilter === 'month' && !(r.days >= 0 && r.days <= 30)) return false;
      if (q && !(r.company.toLowerCase().includes(q) || r.compliance.toLowerCase().includes(q) || (r.formCode || '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [allRows, selectedFilter, searchQuery]);

  const overdueCritical = useMemo(() => allRows.filter((a) => a.days < 0).length, [allRows]);
  const dueThisWeek = useMemo(() => allRows.filter((a) => a.days >= 0 && a.days <= 7).length, [allRows]);
  const dueThisMonth = useMemo(() => allRows.filter((a) => a.days >= 0 && a.days <= 30).length, [allRows]);
  const totalPenalty = useMemo(() => allRows.reduce((sum, a) => sum + (a.penalty || 0), 0), [allRows]);

  const kpis = useMemo(() => [
    { label: 'Overdue Critical', value: overdueCritical, sub: 'Penalty risk', color: '#9a564a', icon: FiAlertTriangle },
    { label: 'Due This Week', value: dueThisWeek, sub: 'Within 7 days', color: '#8a6a1f', icon: FiClock },
    { label: 'Due This Month', value: dueThisMonth, sub: 'Within 30 days', color: '#3a4a66', icon: FiCalendar },
    {
      label: 'Total Penalty',
      value: totalPenalty > 0 ? `₹${totalPenalty.toLocaleString('en-IN')}` : '₹0',
      sub: totalPenalty > 0 ? 'Estimated exposure' : 'All clear',
      color: totalPenalty > 0 ? '#9a564a' : '#4a6b3f',
      icon: FiTrendingUp,
      compact: true,
    },
  ], [overdueCritical, dueThisWeek, dueThisMonth, totalPenalty]);

  const handleSendReminders = useCallback(() => {
    showToast(`Reminders sent for ${rows.length} alert${rows.length !== 1 ? 's' : ''}`, 'success');
  }, [rows, showToast]);

  // Real .xlsx export — everything a CA/CS needs to act, plus a Summary sheet.
  // xlsx is dynamically imported so the library only loads on click.
  const handleExport = useCallback(async () => {
    try {
      const XLSX = await import('xlsx');
      const sevLabel = (r) => (r.days < 0 ? 'Overdue' : r.days <= 7 ? 'Due this week' : r.days <= 30 ? 'Due this month' : 'Upcoming');
      const agmStatus = (r) => (r.agmNotSet ? 'AGM date missing' : r.agmNotSetForCurrentFy ? 'AGM outstanding (this FY)' : 'Set');

      const data = rows.map((r) => ({
        'Company': r.company,
        'Entity Type': r.type,
        'Location': locationOf(r),
        'Form': r.formCode,
        'Filing': r.item || r.compliance,
        'Statutory Ref': r.section,
        'Severity': sevLabel(r),
        'Due Date': r.dueDate,
        'Days Remaining': r.days, // negative = days overdue
        'Penalty / Day (₹)': r.penaltyPerDay || 0,
        'Est. Penalty (₹)': r.penalty || 0,
        'Penalty Note': r.penaltyNote,
        'AGM Status': agmStatus(r),
        'Deadline Basis': r.estimated ? 'Estimated (statutory AGM assumed)' : 'Exact',
        'Next Step': r.action,
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      ws['!cols'] = [
        { wch: 34 }, { wch: 12 }, { wch: 18 }, { wch: 11 }, { wch: 30 }, { wch: 16 },
        { wch: 13 }, { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 26 },
        { wch: 22 }, { wch: 28 }, { wch: 10 },
      ];
      const totalPen = rows.reduce((s, r) => s + (r.penalty || 0), 0);
      XLSX.utils.sheet_add_aoa(ws, [['TOTAL', '', '', '', '', '', '', '', '', '', totalPen, '', '', '', '']], { origin: -1 });

      const wsSummary = XLSX.utils.aoa_to_sheet([
        ['Compliance Alerts — Export Summary'],
        ['Generated', new Date().toLocaleString('en-IN')],
        ['Scope', `${rows.length} alert${rows.length !== 1 ? 's' : ''}`],
        [],
        ['Overdue', rows.filter((r) => r.days < 0).length],
        ['Due this week', rows.filter((r) => r.days >= 0 && r.days <= 7).length],
        ['Due this month', rows.filter((r) => r.days > 7 && r.days <= 30).length],
        ['Total estimated penalty (₹)', totalPen],
        [],
        ['Reading the Alerts sheet'],
        ['Days Remaining', 'Negative values = days overdue'],
        ['Deadline Basis', '"Estimated" rows assume the statutory AGM deadline — set the actual AGM date for exact dues'],
        ['Penalties', 'Statutory-rate estimates; verify on MCA before filing'],
      ]);
      wsSummary['!cols'] = [{ wch: 30 }, { wch: 60 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Alerts');
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
      XLSX.writeFile(wb, `compliance-alerts-${new Date().toISOString().split('T')[0]}.xlsx`);
      showToast(`Exported ${rows.length} alert${rows.length !== 1 ? 's' : ''} to Excel`, 'success');
    } catch (err) {
      showToast(err?.message || 'Export failed', 'error');
    }
  }, [rows, showToast]);

  const filters = [
    { key: 'all', label: 'All', count: allRows.length },
    { key: 'overdue', label: 'Overdue', count: overdueCritical },
    { key: 'week', label: 'Week', count: dueThisWeek },
    { key: 'month', label: 'Month', count: dueThisMonth },
  ];

  return (
    <AppLayout pageTitle="Alerts & Dues" hasDashboardAlert={overdueCritical > 0}>
      <PageHeader
        title="Alerts & Due Compliance"
        subtitle={alerts.length > 0
          ? `Real-time monitoring — ${alerts.length} alert${alerts.length !== 1 ? 's' : ''} across your portfolio`
          : 'No overdue or upcoming deadlines detected. All companies are compliant.'}
        actions={(
          <>
            <Button variant="outline" size="sm" leftIcon={<FiBell size={15} />} onClick={handleSendReminders} disabled={rows.length === 0} className="!rounded-xl">
              Send Reminders
            </Button>
            <Button variant="gold" size="sm" leftIcon={<FiDownload size={15} />} onClick={handleExport} disabled={rows.length === 0} className="!rounded-xl shadow-sm">
              Export
            </Button>
            <Button variant="outline" size="sm" leftIcon={<FiCalendar size={15} />} onClick={() => navigate(ROUTES.CALENDAR)} className="!rounded-xl">
              View Calendar
            </Button>
          </>
        )}
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5 mb-6">
        {kpis.map((k) => {
          const Ico = k.icon;
          return (
            <div
              key={k.label}
              className="cs-resolution-card rounded-2xl p-4 flex flex-col gap-2 transition-all hover:-translate-y-0.5"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[1.2px] text-[#7a7060] font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>{k.label}</div>
                <span className="shrink-0 grid place-items-center w-7 h-7 rounded-xl bg-[#fdfbf7] border border-[#e2d9c8]" style={{ color: k.color }}>
                  <Ico size={14} />
                </span>
              </div>
              <div className={`${k.compact ? 'text-[26px]' : 'text-[40px]'} leading-none font-bold`} style={{ color: k.color, fontFamily: "'Cormorant Garamond', serif" }}>
                {k.value}
              </div>
              <div className="text-[11px] text-[#7a7060]" style={{ fontFamily: "'Outfit', sans-serif" }}>{k.sub}</div>
            </div>
          );
        })}
      </div>

      {/* Toolbar: heading · search · filter pills */}
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2 mr-auto">
          {alerts.length > 0
            ? <FiAlertTriangle size={16} className="text-[#9a564a]" />
            : <FiCheckCircle size={16} className="text-[#4a6b3f]" />}
          <span className="text-[16px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {alerts.length > 0 ? 'Active Alerts' : 'All Clear'}
          </span>
        </div>

        {/* Search — filters cards by company, form, or compliance item */}
        <div className="relative w-full sm:w-[248px] order-3 sm:order-none">
          <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a7060] pointer-events-none" />
          <input
            type="text"
            placeholder="Search company or form..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-[12.5px] font-semibold bg-white border border-[#e2d9c8] rounded-xl focus:border-[#145f5f] focus:ring-2 focus:ring-[#145f5f]/10 outline-none transition-all"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          />
          {searchQuery && (
            <button type="button" onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#7a7060] hover:text-[#08111f]">
              <FiX size={13} />
            </button>
          )}
        </div>

        {/* Filter segmented control */}
        <div className="flex gap-1 p-[3px] rounded-xl border" style={{ background: '#f0ece2', borderColor: '#ddd6c6' }}>
          {filters.map((f) => {
            const active = selectedFilter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setSelectedFilter(f.key)}
                className="px-[10px] py-[5px] rounded-lg text-[11px] font-semibold transition-all duration-150 active:scale-[0.97]"
                style={{ background: active ? '#145f5f' : 'transparent', color: active ? '#fff' : '#5a5145' }}
              >
                {f.label} ({f.count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Alert cards */}
      {rows.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map((row) => {
            const dc = daysChip(row.days);
            const loc = locationOf(row);
            return (
              <div
                key={row.id}
                className="cs-resolution-card group rounded-[16px] p-4 h-full flex flex-col"
              >
                {/* Identity + urgency */}
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-[13px] leading-snug text-[#08111f] flex-1 min-w-0 break-words">{row.company}</h3>
                  <Chip bg={dc.bg} color={dc.color}>{dc.label}</Chip>
                </div>

                {/* Badges */}
                <div className="flex items-center gap-1.5 flex-wrap mt-2">
                  <Chip tone="#7a7060">{row.type}</Chip>
                  {row.agmNotSet && <Chip bg="#f3ebd7" color="#8a6a1f">AGM missing</Chip>}
                  {!row.agmNotSet && row.agmNotSetForCurrentFy && <Chip bg="#e8ecf3" color="#3a4a66">AGM outstanding</Chip>}
                </div>

                {/* Compliance item */}
                <div className="mt-3 text-[13px] font-semibold text-[#08111f] leading-snug break-words">{row.compliance}</div>

                {/* Meta */}
                <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-[#7a7060]">
                  <FiCalendar size={12} className="shrink-0" />
                  <span>Due {row.dueDate}</span>
                  {loc && <span className="truncate">· {loc}</span>}
                </div>

                {/* Footer: penalty + action */}
                <div className="mt-auto pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: 'rgba(226,217,200,0.5)' }}>
                  <div className="min-w-0">
                    <div className="text-[9px] uppercase tracking-[0.6px] text-[#7a7060] font-semibold">Est. penalty</div>
                    <div className="text-[13px] font-bold tabular-nums" style={{ color: row.penalty > 0 ? '#9a564a' : '#4a6b3f' }}>
                      {'₹'}{Number(row.penalty || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <Button
                    variant={row.days < 0 ? 'danger' : 'outline'}
                    size="sm"
                    leftIcon={<FiFileText size={14} />}
                    className="!rounded-xl"
                    onClick={() => showToast(`${row.action}: ${row.company}`, 'info')}
                  >
                    {row.action}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        allRows.length > 0 ? (
          <EmptyState
            variant="filter"
            illustration={<IlloSearch />}
            title="No alerts match these filters"
            subtitle="Try clearing the search or switching the severity filter to see the rest of your portfolio."
            action={<Button variant="outline" size="sm" onClick={() => { setSelectedFilter('all'); setSearchQuery(''); }}>Clear filters</Button>}
          />
        ) : (
          <EmptyState
            variant="hero"
            illustration={<IlloAllClear />}
            title="Nothing due, nothing overdue"
            subtitle="This page becomes your command center the moment a deadline approaches — every filing, penalty and AGM in one place. Add companies to start tracking."
            action={<Button variant="primary" size="sm" onClick={() => navigate(ROUTES.COMPANIES, { state: { openAddCompany: true } })}>Add a company</Button>}
          />
        )
      )}
    </AppLayout>
  );
}
